import type { FC, SVGProps } from "react";

import AnalyticsIcon from "@/assets/images/analytics.svg";
import BeosIcon from "@/assets/images/beos.svg";
import BookingsIcon from "@/assets/images/bookings.svg";
import CalendarIcon from "@/assets/images/calendar.svg";
import CashlessAiIcon from "@/assets/images/cashless-ai-v2.svg";
import CustomersIcon from "@/assets/images/customers.svg";
// Dashboard reuses the existing grid icon; Guest Lists reuses the Customers icon.
import DashboardIcon from "@/assets/images/dashboard-icon.svg";
import FinanceIcon from "@/assets/images/finance.svg";
import GetHelpIcon from "@/assets/images/get-help.svg";
import GuestCheckInIcon from "@/assets/images/guest-check-in.svg";
import LeadPipelineIcon from "@/assets/images/lead-pipeline.svg";
import MenuPackagesIcon from "@/assets/images/menu-packages.svg";
import SettingsIcon from "@/assets/images/settings.svg";
import VenuesSpacesIcon from "@/assets/images/venues-spaces.svg";

export type SvgIcon = FC<SVGProps<SVGSVGElement>>;

export interface NavItem {
  label: string;
  href: string;
  /** SVG-component icon (rendered inline, inherits color). */
  icon?: SvgIcon;
  /** Optional RBAC codename — item hidden unless the user holds it. */
  requiredPermission?: string;
  /**
   * Show/hide toggle. Items are visible by default; set `enabled: false` to hide
   * one (e.g. a route that isn't built yet). Only enabled items render.
   */
  enabled?: boolean;
}

export interface NavGroup {
  title?: string;
  items: NavItem[];
}

const RAW_NAV_GROUPS: NavGroup[] = [
  {
    items: [
      { label: "Cashless Ai", href: "/ai-agent", icon: CashlessAiIcon },
      { label: "Dashboard", href: "/dashboard", icon: DashboardIcon },
      { label: "Calendar", href: "/calendar", icon: CalendarIcon },
    ],
  },
  {
    title: "SALES",
    items: [
      {
        label: "Lead Pipeline",
        href: "/lead-pipeline",
        icon: LeadPipelineIcon,
      },
      { label: "Bookings", href: "/bookings", icon: BookingsIcon },
      { label: "Customers", href: "/customers", icon: CustomersIcon },
    ],
  },
  {
    title: "EVENTS",
    items: [
      { label: "BEOs", href: "/beos", icon: BeosIcon },
      {
        label: "Guest Check-in",
        href: "/guest-check-in",
        icon: GuestCheckInIcon,
        enabled: false,
      },
      {
        label: "Guest Lists",
        href: "/guest-lists",
        icon: CustomersIcon,
        enabled: false,
      },
    ],
  },
  {
    title: "OPERATIONS",
    items: [
      {
        label: "Menu & Packages",
        href: "/menu-packages",
        icon: MenuPackagesIcon,
      },
      {
        label: "Venues & Spaces",
        href: "/venues-spaces",
        icon: VenuesSpacesIcon,
      },
    ],
  },
  {
    title: "INSIGHTS",
    items: [
      { label: "Finance", href: "/finance", icon: FinanceIcon },
      { label: "Analytics", href: "/analytics", icon: AnalyticsIcon },
    ],
  },
  {
    title: "OTHERS",
    items: [
      { label: "Settings", href: "/settings", icon: SettingsIcon },
      { label: "Get Help", href: "/help", icon: GetHelpIcon },
    ],
  },
];

/**
 * Visible nav config — only enabled items render (an item is enabled unless it
 * sets `enabled: false`), and any group left with no items is dropped. Flip an
 * item's `enabled` in `RAW_NAV_GROUPS` to show/hide it.
 */
export const eventNavGroups: NavGroup[] = RAW_NAV_GROUPS.map((group) => ({
  ...group,
  items: group.items.filter((item) => item.enabled !== false),
})).filter((group) => group.items.length > 0);
